import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import AsyncSelect from "react-select/async";
import toast from "react-hot-toast";
import dayjs from "dayjs";
import { ArrowLeft } from "react-feather";
import {
  useLazyGetCustomersQuery,
  useGetCustomerQuery,
  useBulkCreateAssignmentsMutation,
} from "../customersApi";
import { useCanEditGuardAssignments } from "../useGuardPermissions";
import { paths } from "../../../../routes/paths";

const AREA_OPTIONS = ["Main Gate", "Perimeter", "Warehouse", "Reception", "Parking"];

// Customer names run 250-300 characters, and the branch-wise customer table
// has 5000+ rows, so this searches server-side (react-select/async) rather
// than listing everything.
const customerSelectStyles = {
  menu: (base) => ({ ...base, minWidth: 420, maxWidth: 640 }),
  option: (base) => ({ ...base, whiteSpace: "normal", wordBreak: "break-word" }),
};

const emptyForm = {
  area: "",
  dayNight: "Day",
  assignmentName: "",
  noOfGuards: 0,
  isTemp: false,
  fromDate: dayjs().format("YYYY-MM-DD"),
  days: 30,
  locationId: "",
};

// Dedicated screen (own route, reached only via a button - never a tab or
// an expand panel) mirroring texas_master's Frm_Bulk_Guards, now against
// the live POST .../assignments/bulk endpoint (handoff §9.7 Guard
// Assignments follow-up) - a single server-side call instead of the
// client-side merge this screen used before that endpoint existed.
const BulkGuardsPage = () => {
  const { branchId: routeBranchId, custId: routeCustId } = useParams();
  const canEdit = useCanEditGuardAssignments();
  const [customerOption, setCustomerOption] = useState(null);
  const [triggerSearch] = useLazyGetCustomersQuery();
  const [form, setForm] = useState(emptyForm);

  const branchId = customerOption?.branchId || routeBranchId;
  const custId = customerOption?.value || routeCustId;
  const { data: detail } = useGetCustomerQuery(
    { branchId, custId },
    { skip: !branchId || !custId }
  );
  const [bulkCreate, { isLoading: isSaving }] = useBulkCreateAssignmentsMutation();

  const locations = detail?.locations || [];
  const hasMultipleLocations = locations.length > 1;

  // Pre-fill the label for the route-provided customer once its detail loads.
  useEffect(() => {
    if (!customerOption && routeCustId && detail?.customer) {
      setCustomerOption({
        value: detail.customer.custId,
        branchId: detail.customer.branchId,
        label: detail.customer.custName,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail]);

  // Multi-location customers (handoff §19.6/§9.7 update, added 2026-08-05) -
  // default/reset the Location picker to this customer's primary whenever
  // the selected customer changes (or on first load), since a different
  // customer has a completely different set of locationIds.
  useEffect(() => {
    if (!detail?.locations?.length) return;
    const stillValid = detail.locations.some((l) => l.locationId === form.locationId);
    if (!stillValid) {
      const primary = detail.locations.find((l) => l.isPrimary) || detail.locations[0];
      setForm((prev) => ({ ...prev, locationId: primary.locationId }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail]);

  const loadOptions = async (inputValue) => {
    if (!inputValue || inputValue.trim().length === 0) return [];
    const { data = [] } = await triggerSearch({ search: inputValue.trim(), isActive: true });
    return data.map((c) => ({
      value: c.custId,
      branchId: c.branchId,
      label: c.custName || `(unnamed) #${c.custCode}`,
    }));
  };

  const setField = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  // Keeps the current Location selection across a clear - staff commonly
  // add several bulk batches to the same site in a row.
  const handleClear = () => setForm((prev) => ({ ...emptyForm, locationId: prev.locationId }));

  const handleSave = async () => {
    if (!custId || !branchId) {
      toast.error("Select a customer");
      return;
    }
    if (!form.area || !form.assignmentName.trim()) {
      toast.error("Area and assignment name are required");
      return;
    }
    const count = Number(form.noOfGuards) || 0;
    if (count <= 0) {
      toast.error("No of guards must be greater than 0");
      return;
    }
    try {
      await bulkCreate({
        branchId,
        custId,
        locationId: form.locationId || undefined,
        assignmentNamePrefix: form.assignmentName.trim(),
        assignmentArea: form.area,
        dayNight: form.dayNight,
        count,
        effectiveDate: dayjs(form.fromDate).format("YYYY-MM-DDT00:00:00"),
        isTemp: form.isTemp,
        tempDays: form.isTemp ? Number(form.days) || 0 : null,
      }).unwrap();
      toast.success(`${count} guarding posts created`);
      handleClear();
    } catch (error) {
      toast.error(error?.message || "Could not create guarding posts");
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <Link
                to={routeCustId ? paths.customerGuarding(routeBranchId, routeCustId) : paths.customers}
                className="d-inline-flex align-items-center mb-2"
              >
                <ArrowLeft size={16} className="me-1" /> Back
              </Link>
              <h4>Bulk Guard Entry</h4>
              <h6>Create multiple guarding posts for a customer in one go</h6>
            </div>
          </div>
        </div>

        {!canEdit && (
          <div className="alert alert-info">
            You have read-only access. Bulk entry requires the Deployment.Edit permission.
          </div>
        )}

        <div className="card mb-4">
          <div className="card-body">
            <div className="mb-4" style={{ maxWidth: 520 }}>
              <label className="form-label">Customer</label>
              <AsyncSelect
                classNamePrefix="react-select"
                styles={customerSelectStyles}
                cacheOptions
                placeholder="Search customer by name..."
                loadOptions={loadOptions}
                value={customerOption}
                onChange={setCustomerOption}
                isDisabled={!canEdit}
                noOptionsMessage={({ inputValue }) => (inputValue ? "No matching customers" : "Type to search customers")}
              />
            </div>

            {hasMultipleLocations && (
              <div className="mb-4" style={{ maxWidth: 260 }}>
                <label className="form-label">Location</label>
                <select
                  className="form-control"
                  value={form.locationId}
                  onChange={setField("locationId")}
                  disabled={!canEdit}
                >
                  {locations.map((loc) => (
                    <option key={loc.locationId} value={loc.locationId}>
                      {loc.siteLabel}{loc.isPrimary ? " (Primary)" : ""}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="d-flex flex-wrap gap-4 mb-4">
              <div style={{ width: 220 }}>
                <label className="form-label">Area</label>
                <select className="form-control" value={form.area} onChange={setField("area")} disabled={!canEdit}>
                  <option value="">--Select--</option>
                  {AREA_OPTIONS.map((area) => (
                    <option key={area} value={area}>{area}</option>
                  ))}
                </select>
              </div>
              <div style={{ width: 130 }}>
                <label className="form-label">Day / Night</label>
                <select className="form-control" value={form.dayNight} onChange={setField("dayNight")} disabled={!canEdit}>
                  <option value="Day">Day</option>
                  <option value="Night">Night</option>
                </select>
              </div>
            </div>

            <div className="d-flex flex-wrap align-items-start gap-4 mb-4">
              <div style={{ flex: "1 1 320px" }}>
                <label className="form-label">Assignment</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="e.g. Guard"
                  value={form.assignmentName}
                  onChange={setField("assignmentName")}
                  disabled={!canEdit}
                />
                <small className="text-muted">Posts will be named &quot;{form.assignmentName || "Guard"} 1&quot;, &quot;2&quot;, ...</small>
              </div>
              <div style={{ width: 100 }}>
                <label className="form-label">No Of Guards</label>
                <input type="number" className="form-control" maxLength={3} value={form.noOfGuards} onChange={setField("noOfGuards")} disabled={!canEdit} />
              </div>
              <div>
                <label className="form-label d-block">Duration</label>
                <div className="d-flex gap-3" style={{ height: 38, alignItems: "center" }}>
                  <div className="form-check mb-0">
                    <input
                      type="radio"
                      className="form-check-input"
                      id="bulk-permanent"
                      checked={!form.isTemp}
                      onChange={() => setForm((prev) => ({ ...prev, isTemp: false }))}
                      disabled={!canEdit}
                    />
                    <label className="form-check-label" htmlFor="bulk-permanent">Permanent</label>
                  </div>
                  <div className="form-check mb-0">
                    <input
                      type="radio"
                      className="form-check-input"
                      id="bulk-temp"
                      checked={form.isTemp}
                      onChange={() => setForm((prev) => ({ ...prev, isTemp: true }))}
                      disabled={!canEdit}
                    />
                    <label className="form-check-label" htmlFor="bulk-temp">Temp</label>
                  </div>
                </div>
              </div>
            </div>

            <div className="d-flex flex-wrap align-items-start gap-4 mb-4">
              <div style={{ width: 200 }}>
                <label className="form-label">From Date</label>
                <input type="date" className="form-control" value={form.fromDate} onChange={setField("fromDate")} disabled={!canEdit} />
              </div>
              <div style={{ width: 160 }}>
                <label className="form-label">No Of Days</label>
                <input type="number" className="form-control" value={form.days} onChange={setField("days")} disabled={!canEdit} />
                <small className="text-muted">Used when Temp is selected (ignored for Permanent).</small>
              </div>
            </div>

            {canEdit && (
              <div className="d-flex gap-2">
                <button type="button" className="btn btn-primary" onClick={handleSave} disabled={isSaving}>Save</button>
                <button type="button" className="btn btn-secondary" onClick={handleClear}>Clear</button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkGuardsPage;
